// client/src/components/FaceCollectionSearch.jsx
import React, { useState, useEffect } from 'react';
import { 
  Container, Form, Button, Image, Alert, Spinner, 
  Card, ListGroup, Badge, Row, Col, Tabs, Tab, Nav,
  ProgressBar, Stack, OverlayTrigger, Tooltip
} from 'react-bootstrap';
import BulkFaceSearch from './BulkFaceSearch';
import { enhancedSearchService } from '../services/enhancedSearchService';
import { secondApiService } from '../services/secondApiService';
import { rbiCollectionService } from '../services/rbiCollectionService';

const FaceCollectionSearch = () => {
  const [searchMode, setSearchMode] = useState('single');
  const [metadataVisibility, setMetadataVisibility] = useState({});
  // State for image uploading
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  
  // State for search options
  const [searchOptions, setSearchOptions] = useState({
    rbiCollection: true,
    thirdPartyAPI1: false,
    thirdPartyAPI2: false
  });
  
  // State for search parameters
  const [similarityThreshold, setSimilarityThreshold] = useState(70);
  const [maxFaces, setMaxFaces] = useState(10);
  
  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // API-specific states
  const [rbiResults, setRbiResults] = useState(null);
  const [rbiLoading, setRbiLoading] = useState(false);
  const [rbiError, setRbiError] = useState(null);
  
  const [api1Results, setApi1Results] = useState(null);
  const [api1Loading, setApi1Loading] = useState(false);
  const [api1Error, setApi1Error] = useState(null);
  
  const [api2Results, setApi2Results] = useState(null);
  const [api2Loading, setApi2Loading] = useState(false);
  const [api2Error, setApi2Error] = useState(null);

   // Combined results
  const [combinedResults, setCombinedResults] = useState([]);
  const [activeTab, setActiveTab] = useState('combined');
  
  // Get combined results whenever individual results change
  useEffect(() => {
    // Combine all results
    const allResults = [];
    
    // Add RBI Collection results
    if (rbiResults && rbiResults.success && rbiResults.matches) {
      allResults.push(...rbiResults.matches.map(match => ({
        ...match,
        source: 'Custom Search'
      })));
    }
    
    // Add Third-party API 1 results
    if (api1Results && api1Results.enhancedResults && api1Results.matches) {
      allResults.push(...api1Results.matches.map(match => ({
        ...match,
        source: 'Social Enhanced 1'
      })));
    }
    
    // Add Third-party API 2 results
    if (api2Results && api2Results.success && api2Results.matches) {
      allResults.push(...api2Results.matches.map(match => ({
        ...match,
        source: 'Web Enhanced 2'
      })));
    }
    
    // Sort by score descending
    allResults.sort((a, b) => b.score - a.score);
    
    setCombinedResults(allResults);
  }, [rbiResults, api1Results, api2Results]);
  
  // Handle image selection
  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      const selectedImage = e.target.files[0];
      setImage(selectedImage);
      setImagePreview(URL.createObjectURL(selectedImage));
      
      // Reset results
      setRbiResults(null);
      setApi1Results(null);
      setApi2Results(null);
      setCombinedResults([]);
      setError(null);
    }
  };
  
  // Handle search option changes
  const handleSearchOptionChange = (e) => {
    const { name, checked } = e.target;
    setSearchOptions(prev => ({
      ...prev,
      [name]: checked
    }));
  };
  
  // Perform searches
  const performSearches = async () => {
    if (!image) {
      setError("Please select an image first");
      return;
    }
    
    if (!searchOptions.rbiCollection && !searchOptions.thirdPartyAPI1 && !searchOptions.thirdPartyAPI2) {
      setError("Please select at least one search option");
      return;
    }
    
    setLoading(true);
    setError(null);
    
    // Custom Search
    if (searchOptions.rbiCollection) {
      setRbiLoading(true);
      setRbiError(null);
      
      try {
        const results = await rbiCollectionService.searchCollection(
          image,
          maxFaces,
          similarityThreshold
        );
        setRbiResults(results);
      } catch (err) {
        console.error("Custom search error:", err);
        setRbiError(`Custom search failed: ${err.message}`);
      } finally {
        setRbiLoading(false);
      }
    }
    
    // Third-party API 1 Search
    if (searchOptions.thirdPartyAPI1) {
      setApi1Loading(true);
      setApi1Error(null);
      
      try {
        const results = await enhancedSearchService.analyzeImage(image);
        setApi1Results(results);
      } catch (err) {
        console.error("Social Enhanced 1 search error:", err);
        setApi1Error(`Social Enhanced 1 search failed: ${err.message}`);
      } finally {
        setApi1Loading(false);
      }
    }
    
    // Third-party API 2 Search
    if (searchOptions.thirdPartyAPI2) {
      setApi2Loading(true);
      setApi2Error(null);
      
      try {
        const results = await secondApiService.searchFaces(image);
        setApi2Results(results);
      } catch (err) {
        console.error("RBI Web Enhanced 2 search error:", err);
        setApi2Error(`RBI Web Enhanced 2 search failed: ${err.message}`);
      } finally {
        setApi2Loading(false);
      }
    }
    
    setLoading(false);
  };
  
  // Get confidence color class based on similarity score
  const getSimilarityColorClass = (similarity) => {
    if (similarity >= 90) return 'confidence-high';
    if (similarity >= 70) return 'confidence-medium';
    return 'confidence-low';
  };
  
  // Get variant based on similarity score
  const getSimilarityVariant = (similarity) => {
    if (similarity >= 90) return 'success';
    if (similarity >= 70) return 'info';
    if (similarity >= 50) return 'warning';
    return 'danger';
  };

  // CSV Export function
  const exportToCSV = (results, filename = 'face-search-results.csv') => {
    if (!results || results.length === 0) {
      setError('No results to export');
      return;
    }
    
    // Gather all possible fields from all results (some results might have different fields)
    const excludeFields = ['imageUrl', 'imageData', 'fullImageData', 'thumbnail'];
    const allFields = new Set(['source', 'score', 'url']);
    
    // Add any metadata fields that exist in any of the results
    results.forEach(result => {
      if (result.metadata) {
        Object.keys(result.metadata).forEach(key => {
          allFields.add(`metadata_${key}`);
        });
      }
    });
    
    const headers = Array.from(allFields);
    
    // Create CSV header row
    let csvContent = headers.join(',') + '\n';
    
    // Add data rows
    results.forEach(result => {
      const row = headers.map(header => {
        let cellValue;
        
        // Handle metadata fields
        if (header.startsWith('metadata_') && result.metadata) {
          const metadataKey = header.replace('metadata_', '');
          cellValue = result.metadata[metadataKey];
        } else {
          cellValue = result[header];
        }
        
        // Format values appropriately for CSV
        if (cellValue === null || cellValue === undefined) {
          return '';
        } else if (typeof cellValue === 'object') {
          // Convert objects to JSON strings
          return `"${JSON.stringify(cellValue).replace(/"/g, '""')}"`;
        } else if (typeof cellValue === 'string' && (cellValue.includes(',') || cellValue.includes('"') || cellValue.includes('\n'))) {
          // Escape strings with special characters
          return `"${cellValue.replace(/"/g, '""')}"`;
        } else {
          return cellValue;
        }
      }).join(',');
      
      csvContent += row + '\n';
    });
    
    // Create a blob and download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Render a face match card
  const renderMatchCard = (match, index) => {
    const isMetadataVisible = metadataVisibility[match.id] || false;
  
    return (
      <Col key={match.id || index} md={6} lg={4} className="mb-4">
        <Card className={`match-card ${getSimilarityColorClass(match.score)}`}>
          <Card.Header className="d-flex justify-content-between align-items-center">
            <Badge bg="secondary" pill>Match Found</Badge>
            <Badge bg={getSimilarityVariant(match.score)} pill>
              {match.score.toFixed(1)}% Similarity
            </Badge>
          </Card.Header>
  
          <Card.Body>
            {/* Display the Matched Image */}
            {match.thumbnail && (
  <div className="text-center mb-3">
    <img 
      src={match.thumbnail.startsWith('data:') ? match.thumbnail : match.thumbnail}
      alt={`Match ${index + 1}`}
      style={{ maxHeight: '150px', maxWidth: '100%' }}
      className="match-thumbnail"
      onError={(e) => {
        console.log('Image failed to load:', match.thumbnail);
        e.target.src = "https://redbeardpi.com/images/No_Image_Available.jpg?text=No+Image";
        e.target.alt = "Image unavailable";
      }}
    />
  </div>
            )}
  
            {/* Display the Source URL */}
            {match.url && (
              <ListGroup.Item>
                <strong>Source URL:</strong> <a href={match.url} target="_blank" rel="noopener noreferrer">{match.url}</a>
              </ListGroup.Item>
            )}
  
            {/* Toggle Button to Show/Hide Metadata */}
            <Button
              variant="outline-primary"
              size="sm"
              className="mt-2"
              onClick={() => setMetadataVisibility(prev => ({
                ...prev,
                [match.id]: !isMetadataVisible
              }))}
            >
              {isMetadataVisible ? "Hide Details" : "Show Metadata"}
            </Button>
  
            {/* Metadata (Hidden by Default) */}
            {isMetadataVisible && (
              <ListGroup variant="flush" className="mt-2">
                {match.metadata && Object.entries(match.metadata).map(([key, value]) => (
                  <ListGroup.Item key={key}>
                    <strong>{key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}:</strong>{' '}
                    {typeof value === 'object' ? JSON.stringify(value) : value}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </Card.Body>
        </Card>
      </Col>
    );
  };
  
  // Render combined results
  const renderCombinedResults = () => {
    if (combinedResults.length === 0) {
      if (loading || rbiLoading || api1Loading || api2Loading) {
        return (
          <div className="text-center p-5">
            <Spinner animation="border" role="status" />
            <p className="mt-3">Searching for matches... Do not refresh... Please wait 1-3 minutes...</p>
          </div>
        );
      }
      
      if (rbiError || api1Error || api2Error) {
        return (
          <Alert variant="warning">
            Search completed with errors. Check individual tabs for details.
          </Alert>
        );
      }
      
      if (rbiResults || api1Results || api2Results) {
        return (
          <Alert variant="info">
            No matches found across all selected search providers.
          </Alert>
        );
      }
      
      return null;
    }
    
    return (
      <div className="results-container">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4>Combined Results ({combinedResults.length} matches)</h4>
          <div>
            <Badge bg="secondary" className="me-2">Sorted by similarity</Badge>
            <Button 
              variant="success" 
              size="sm"
              onClick={() => exportToCSV(combinedResults, 'face-search-combined-results.csv')}
              disabled={combinedResults.length === 0}
            >
              Export All
            </Button>
          </div>
        </div>
        
        <Row>
          {combinedResults.map((match, index) => renderMatchCard(match, index))}
        </Row>
      </div>
    );
  };
  
  // Render RBI Collection results
  const renderRbiResults = () => {
    if (rbiLoading) {
      return (
        <div className="text-center p-5">
          <Spinner animation="border" role="status" />
          <p className="mt-3">Searching RBI Collection...</p>
        </div>
      );
    }
    
    if (rbiError) {
      return (
        <Alert variant="danger">
          {rbiError}
        </Alert>
      );
    }
    
    if (!rbiResults) {
      return (
        <Alert variant="info">
          RBI Collection search not performed yet.
        </Alert>
      );
    }
    
    if (!rbiResults.matches || rbiResults.matches.length === 0) {
      return (
        <Alert variant="warning">
          No matches found in RBI Collection.
        </Alert>
      );
    }
    
    return (
      <div className="results-container">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4>Custom Results ({rbiResults.matches.length} matches)</h4>
          <Button 
            variant="success" 
            size="sm"
            onClick={() => exportToCSV(rbiResults.matches.map(match => ({
              ...match,
              source: 'Custom Search'
            })), 'face-search-custom-results.csv')}
            disabled={!rbiResults.matches || rbiResults.matches.length === 0}
          >
            Export to CSV
          </Button>
        </div>
        
        <Row className="mt-4">
          {rbiResults.matches.map((match, index) => renderMatchCard({
            ...match,
            source: 'Custom Search'
          }, index))}
        </Row>
        
        {rbiResults.searchInfo && (
          <div className="mt-4">
            <h5>Search Details</h5>
            <Card>
              <Card.Body>
                <ListGroup variant="flush">
                  <ListGroup.Item>
                    <strong>Confidence:</strong> {rbiResults.searchInfo.searchedFaceConfidence?.toFixed(2)}%
                  </ListGroup.Item>
                  {rbiResults.searchInfo.searchedFaceBoundingBox && (
                    <ListGroup.Item>
                      <strong>Bounding Box:</strong>
                      <ul className="mt-2 mb-0">
                        <li>Left: {rbiResults.searchInfo.searchedFaceBoundingBox.Left?.toFixed(4)}</li>
                        <li>Top: {rbiResults.searchInfo.searchedFaceBoundingBox.Top?.toFixed(4)}</li>
                        <li>Width: {rbiResults.searchInfo.searchedFaceBoundingBox.Width?.toFixed(4)}</li>
                        <li>Height: {rbiResults.searchInfo.searchedFaceBoundingBox.Height?.toFixed(4)}</li>
                      </ul>
                    </ListGroup.Item>
                  )}
                </ListGroup>
              </Card.Body>
            </Card>
          </div>
        )}
      </div>
    );
  };
  
  // Render Third-party API 1 results
  const renderApi1Results = () => {
    if (api1Loading) {
      return (
        <div className="text-center p-5">
          <Spinner animation="border" role="status" />
          <p className="mt-3">Searching RBI Social Enhanced 1...</p>
        </div>
      );
    }
    
    if (api1Error) {
      return (
        <Alert variant="danger">
          {api1Error}
        </Alert>
      );
    }
    
    if (!api1Results) {
      return (
        <Alert variant="info">
          Social Enhanced 1 search not performed yet.
        </Alert>
      );
    }
    
    if (!api1Results.matches || api1Results.matches.length === 0) {
      return (
        <Alert variant="warning">
          No matches found with RBI Social Enhanced 1.
        </Alert>
      );
    }
    
    return (
      <div className="results-container">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4>Social Enhanced 1 Results ({api1Results.matches.length} matches)</h4>
          <Button 
            variant="success" 
            size="sm"
            onClick={() => exportToCSV(api1Results.matches.map(match => ({
              ...match,
              source: 'Social Enhanced 1'
            })), 'face-search-social-results.csv')}
            disabled={!api1Results.matches || api1Results.matches.length === 0}
          >
            Export to CSV
          </Button>
        </div>
        
        <Row className="mt-4">
          {api1Results.matches.map((match, index) => renderMatchCard({
            ...match,
            source: 'Third-party API 1'
          }, index))}
        </Row>
      </div>
    );
  };
  
  // Render Third-party API 2 results
  const renderApi2Results = () => {
    if (api2Loading) {
      return (
        <div className="text-center p-5">
          <Spinner animation="border" role="status" />
          <p className="mt-3">Searching with RBI Web Enhanced 2...</p>
        </div>
      );
    }
    
    if (api2Error) {
      return (
        <Alert variant="danger">
          {api2Error}
        </Alert>
      );
    }
    
    if (!api2Results) {
      return (
        <Alert variant="info">
          RBI Web Enhanced 2 search not performed yet.
        </Alert>
      );
    }
    
    if (!api2Results.matches || api2Results.matches.length === 0) {
      return (
        <Alert variant="warning">
          No matches found with RBI Web Enhanced 2.
        </Alert>
      );
    }
    
    return (
      <div className="results-container">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4>Web Enhanced 2 Results ({api2Results.matches.length} matches)</h4>
          <Button 
            variant="success" 
            size="sm"
            onClick={() => exportToCSV(api2Results.matches.map(match => ({
              ...match,
              source: 'Web Enhanced 2'
            })), 'face-search-web-results.csv')}
            disabled={!api2Results.matches || api2Results.matches.length === 0}
          >
            Export to CSV
          </Button>
        </div>
        
        <Row className="mt-4">
          {api2Results.matches.map((match, index) => renderMatchCard({
            ...match,
            source: 'Third-party API 2'
          }, index))}
        </Row>
      </div>
    );
  };
  
  return (
    <Container>
      <h2 className="mb-4">Advanced Face Search</h2>

      <Nav
        variant="tabs"
        activeKey={searchMode}
        onSelect={key => setSearchMode(key || 'single')}
        className="mb-4"
      >
        <Nav.Item>
          <Nav.Link eventKey="single">Single Search</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="bulk">Bulk Search</Nav.Link>
        </Nav.Item>
      </Nav>

      {searchMode === 'bulk' ? (
        <BulkFaceSearch />
      ) : (
        <>
      
      <Alert variant="info" className="mb-4">
        <Alert.Heading>Multiple Face Search Options</Alert.Heading>
        <p>
          Upload a single face image and select one or more search options to find potential matches.
          Results will be combined and ranked by similarity percentage. Allow 1-3 minutes for results.
        </p>
      </Alert>
      
      <Row>
        <Col lg={4}>
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Upload Image</h5>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>Select an image containing only one single face to search</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </Form.Group>
              
              {imagePreview && (
                <div className="text-center mt-3">
                  <Image
                    src={imagePreview}
                    className="image-preview"
                    alt="Preview"
                    fluid
                  />
                </div>
              )}
            </Card.Body>
          </Card>
        
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Search Options</h5>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>Select Search Source(s)</Form.Label>
                <div className="d-grid gap-2">
                  <Form.Check 
                    type="checkbox"
                    id="rbi-collection"
                    label="Custom Search"
                    name="rbiCollection"
                    checked={searchOptions.rbiCollection}
                    onChange={handleSearchOptionChange}
                    className="mb-2"
                  />
                  <Form.Check 
                    type="checkbox"
                    id="third-party-api1"
                    label="Social Enhanced 1"
                    name="thirdPartyAPI1"
                    checked={searchOptions.thirdPartyAPI1}
                    onChange={handleSearchOptionChange}
                    className="mb-2"
                  />
                  <Form.Check 
                    type="checkbox"
                    id="third-party-api2"
                    label="Web Enhanced 2"
                    name="thirdPartyAPI2"
                    checked={searchOptions.thirdPartyAPI2}
                    onChange={handleSearchOptionChange}
                    className="mb-2"
                  />
                </div>
              </Form.Group>
              
              <hr />
              
              <Form.Group className="mb-3">
                <Form.Label>Similarity Threshold: {similarityThreshold}%</Form.Label>
                <Form.Range
                  min={1}
                  max={100}
                  value={similarityThreshold}
                  onChange={(e) => setSimilarityThreshold(parseInt(e.target.value))}
                  className="mb-2"
                />
                <Form.Text>
                  Only show matches above this similarity percentage.
                </Form.Text>
              </Form.Group>
              
              <div className="d-grid gap-2">
                <Button
                  variant="primary"
                  onClick={performSearches}
                  disabled={!image || loading || (!searchOptions.rbiCollection && !searchOptions.thirdPartyAPI1 && !searchOptions.thirdPartyAPI2)}
                >
                  {loading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Searching...Please allow up to 3-minutes for your search to complete. Do not refresh.
                    </>
                  ) : (
                    'Search Faces'
                  )}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={8}>
          {error && (
            <Alert variant="danger" className="mb-4">
              {error}
            </Alert>
          )}
          
          <Card>
            <Card.Header>
              <Tabs
                id="results-tabs"
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                className="mb-0"
              >
                <Tab 
                  eventKey="combined" 
                  title={
                    <Stack direction="horizontal" gap={2}>
                      <span>Combined Results</span>
                      {combinedResults.length > 0 && (
                        <Badge bg="primary" pill>{combinedResults.length}</Badge>
                      )}
                    </Stack>
                  }
                />
                {searchOptions.rbiCollection && (
                  <Tab 
                    eventKey="rbi" 
                    title={
                      <Stack direction="horizontal" gap={2}>
                        <span>Custom Search</span>
                        {rbiLoading && (
                          <Spinner animation="border" size="sm" />
                        )}
                        {rbiResults?.matches?.length > 0 && (
                          <Badge bg="primary" pill>{rbiResults.matches.length}</Badge>
                        )}
                      </Stack>
                    }
                  />
                )}
                {searchOptions.thirdPartyAPI1 && (
                  <Tab 
                    eventKey="api1" 
                    title={
                      <Stack direction="horizontal" gap={2}>
                        <span>Social</span>
                        {api1Loading && (
                          <Spinner animation="border" size="sm" />
                        )}
                        {api1Results?.matches?.length > 0 && (
                          <Badge bg="primary" pill>{api1Results.matches.length}</Badge>
                        )}
                      </Stack>
                    }
                  />
                )}
                {searchOptions.thirdPartyAPI2 && (
                  <Tab 
                    eventKey="api2" 
                    title={
                      <Stack direction="horizontal" gap={2}>
                        <span>Web</span>
                        {api2Loading && (
                          <Spinner animation="border" size="sm" />
                        )}
                        {api2Results?.matches?.length > 0 && (
                          <Badge bg="primary" pill>{api2Results.matches.length}</Badge>
                        )}
                      </Stack>
                    }
                  />
                )}
              </Tabs>
            </Card.Header>
            <Card.Body>
              {activeTab === 'combined' && renderCombinedResults()}
              {activeTab === 'rbi' && renderRbiResults()}
              {activeTab === 'api1' && renderApi1Results()}
              {activeTab === 'api2' && renderApi2Results()}
            </Card.Body>
          </Card>
        </Col>
      </Row>
        </>
      )}
    </Container>
  );
};

export default FaceCollectionSearch;
